import { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db, httpsCallable, functions } from '../config/firebase';
import {
  CheckCircle,
  XCircle,
  FileText,
  User,
  Mail,
  Calendar,
  Car,
  Eye,
  MessageSquare,
} from 'lucide-react';

const isImageUrl = url => {
  if (!url) return false;
  const cleanUrl = url.split('?')[0] || url;
  return /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(cleanUrl);
};

const DriverVerifications = () => {
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [rejectionComment, setRejectionComment] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [filter, setFilter] = useState('pending'); // 'pending', 'verified', 'rejected', 'all'

  useEffect(() => {
    fetchVerifications();
  }, [filter]);

  const fetchVerifications = async () => {
    try {
      setLoading(true);
      const usersSnapshot = await getDocs(collection(db, 'users'));

      const allVerifications = [];

      for (const userDoc of usersSnapshot.docs) {
        const userData = userDoc.data();
        if (
          userData.driverLicenseFrontUrl &&
          userData.driverVerificationStatus &&
          userData.driverVerificationStatus !== 'notVerified'
        ) {
          // Apply filter
          if (
            filter === 'all' ||
            userData.driverVerificationStatus === filter
          ) {
            allVerifications.push({
              id: userDoc.id,
              userId: userDoc.id,
              ...userData,
            });
          }
        }
      }

      // Sort by submission date (newest first)
      allVerifications.sort((a, b) => {
        const aDate = a.driverVerificationSubmittedAt?.toDate
          ? a.driverVerificationSubmittedAt.toDate().getTime()
          : 0;
        const bDate = b.driverVerificationSubmittedAt?.toDate
          ? b.driverVerificationSubmittedAt.toDate().getTime()
          : 0;
        return bDate - aDate;
      });

      setVerifications(allVerifications);
    } catch (error) {
      console.error('Error fetching driver verifications:', error);
      alert('Failed to fetch driver verifications');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = timestamp => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleApprove = async verification => {
    if (
      !confirm(
        `Are you sure you want to approve driver verification for ${
          verification.firstName || verification.email
        }?`,
      )
    ) {
      return;
    }

    setIsProcessing(true);
    try {
      // Update user document
      await updateDoc(doc(db, 'users', verification.userId), {
        driverVerificationStatus: 'verified',
        driverVerificationApprovedAt: serverTimestamp(),
        driverVerificationRejectedAt: null,
        driverVerificationRejectionComment: null,
      });

      // Send notification to user
      try {
        const sendNotification = httpsCallable(functions, 'sendNotification');
        await sendNotification({
          userId: verification.userId,
          title: 'Driver Verification Approved',
          body: 'Your driver verification has been approved. Your account is now verified as a driver.',
          data: {
            type: 'driver_verification_approved',
            driverVerificationStatus: 'verified',
          },
        });
      } catch (notificationError) {
        console.error('Error sending notification:', notificationError);
        // Don't fail the approval if notification fails
      }

      alert('Driver verification approved successfully');
      setSelectedVerification(null);
      fetchVerifications();
    } catch (error) {
      console.error('Error approving driver verification:', error);
      alert('Failed to approve driver verification');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async verification => {
    if (!rejectionComment.trim()) {
      alert('Please provide a rejection comment');
      return;
    }

    if (
      !confirm(
        `Are you sure you want to reject driver verification for ${
          verification.firstName || verification.email
        }?`,
      )
    ) {
      return;
    }

    setIsProcessing(true);
    try {
      // Update user document
      await updateDoc(doc(db, 'users', verification.userId), {
        driverVerificationStatus: 'rejected',
        driverVerificationRejectedAt: serverTimestamp(),
        driverVerificationRejectionComment: rejectionComment.trim(),
        driverVerificationApprovedAt: null,
        // Clear driver verification files so user can re-upload
        driverLicenseFrontUrl: null,
        driverLicenseFrontName: null,
        driverLicenseBackUrl: null,
        driverLicenseBackName: null,
        vehicleInsuranceUrl: null,
        vehicleInsuranceName: null,
        driverVerificationSubmittedAt: null,
      });

      // Send notification to user
      try {
        const sendNotification = httpsCallable(functions, 'sendNotification');
        await sendNotification({
          userId: verification.userId,
          title: 'Driver Verification Rejected',
          body: `Your driver verification was rejected. Reason: ${rejectionComment.trim()}`,
          data: {
            type: 'driver_verification_rejected',
            driverVerificationStatus: 'rejected',
            rejectionComment: rejectionComment.trim(),
          },
        });
      } catch (notificationError) {
        console.error('Error sending notification:', notificationError);
        // Don't fail the rejection if notification fails
      }

      alert(
        'Driver verification rejected. User can now re-upload their documents.',
      );
      setSelectedVerification(null);
      setRejectionComment('');
      fetchVerifications();
    } catch (error) {
      console.error('Error rejecting driver verification:', error);
      alert('Failed to reject driver verification');
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusBadge = status => {
    const badges = {
      pending: (
        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
          Pending Review
        </span>
      ),
      verified: (
        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
          Verified
        </span>
      ),
      rejected: (
        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
          Rejected
        </span>
      ),
    };
    return badges[status] || badges.pending;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
          <p className="text-gray-600">Loading driver verifications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Driver Verifications
        </h1>
        <p className="text-gray-600">
          Review and approve or reject driver verifications (Driver License &
          Vehicle Insurance)
        </p>
      </div>

      {/* Filter Tabs */}
      <div className="mb-6 flex space-x-2 border-b border-gray-200">
        {['all', 'pending', 'verified', 'rejected'].map(filterOption => (
          <button
            key={filterOption}
            onClick={() => setFilter(filterOption)}
            className={`px-4 py-2 font-semibold text-sm capitalize transition-colors ${
              filter === filterOption
                ? 'border-b-2 border-black text-black'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {filterOption === 'all'
              ? 'All'
              : filterOption === 'pending'
              ? 'Pending'
              : filterOption === 'verified'
              ? 'Verified'
              : 'Rejected'}{' '}
            (
            {filterOption === 'all'
              ? verifications.length
              : verifications.filter(
                  v => v.driverVerificationStatus === filterOption,
                ).length}
            )
          </button>
        ))}
      </div>

      {/* Verifications List */}
      {verifications.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <Car size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500 text-lg">
            No driver verifications found for the selected filter.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {verifications.map(verification => (
                  <tr
                    key={verification.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedVerification(verification)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {verification.photoURL ? (
                          <img
                            className="h-10 w-10 rounded-full object-cover"
                            src={verification.photoURL}
                            alt={verification.firstName || verification.email}
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-yellow-500 flex items-center justify-center">
                            <User size={20} className="text-black" />
                          </div>
                        )}
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {verification.firstName && verification.lastName
                              ? `${verification.firstName} ${verification.lastName}`
                              : verification.username || verification.email}
                          </div>
                          <div className="text-sm text-gray-500">
                            {verification.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(verification.driverVerificationSubmittedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(verification.driverVerificationStatus)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          setSelectedVerification(verification);
                        }}
                        className="text-yellow-600 hover:text-yellow-800 flex items-center"
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Verification Detail Modal */}
      {selectedVerification && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border-2 border-black">
            <div className="p-6 border-b-2 border-black bg-black">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-yellow-400">
                  Driver Verification Details
                </h2>
                <button
                  onClick={() => {
                    setSelectedVerification(null);
                    setRejectionComment('');
                  }}
                  className="text-yellow-400 hover:text-yellow-300"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              {/* User Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  User Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3">
                    {selectedVerification.photoURL ? (
                      <img
                        className="h-16 w-16 rounded-full object-cover border-2 border-yellow-500"
                        src={selectedVerification.photoURL}
                        alt={
                          selectedVerification.firstName ||
                          selectedVerification.email
                        }
                      />
                    ) : (
                      <div className="h-16 w-16 rounded-full bg-yellow-500 flex items-center justify-center border-2 border-black">
                        <User size={32} className="text-black" />
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-gray-500">Name</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {selectedVerification.firstName &&
                        selectedVerification.lastName
                          ? `${selectedVerification.firstName} ${selectedVerification.lastName}`
                          : selectedVerification.username ||
                            selectedVerification.email}
                      </p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Email</p>
                    <p className="text-lg text-gray-900 flex items-center">
                      <Mail size={16} className="mr-2 text-gray-400" />
                      {selectedVerification.email}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Phone Number
                    </p>
                    <p className="text-lg text-gray-900">
                      {selectedVerification.phoneNumber || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Member Since
                    </p>
                    <p className="text-lg text-gray-900 flex items-center">
                      <Calendar size={16} className="mr-2 text-gray-400" />
                      {formatDate(selectedVerification.createdAt)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Verification Information */}
              <div className="border-t-2 border-gray-200 pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Driver Verification Information
                </h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Status</p>
                    <div className="mt-1">
                      {getStatusBadge(
                        selectedVerification.driverVerificationStatus,
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Submitted At
                    </p>
                    <p className="text-lg text-gray-900">
                      {formatDate(
                        selectedVerification.driverVerificationSubmittedAt,
                      )}
                    </p>
                  </div>
                  {selectedVerification.driverVerificationApprovedAt && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Approved At
                      </p>
                      <p className="text-lg text-gray-900">
                        {formatDate(
                          selectedVerification.driverVerificationApprovedAt,
                        )}
                      </p>
                    </div>
                  )}
                  {selectedVerification.driverVerificationRejectedAt && (
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Rejected At
                      </p>
                      <p className="text-lg text-gray-900">
                        {formatDate(
                          selectedVerification.driverVerificationRejectedAt,
                        )}
                      </p>
                      {selectedVerification.driverVerificationRejectionComment && (
                        <div className="mt-2">
                          <p className="text-sm font-medium text-gray-500">
                            Rejection Comment
                          </p>
                          <p className="text-lg text-gray-900 bg-red-50 p-3 rounded-lg border border-red-200">
                            {
                              selectedVerification.driverVerificationRejectionComment
                            }
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Document Preview */}
              {(selectedVerification.driverLicenseFrontUrl ||
                selectedVerification.driverLicenseBackUrl ||
                selectedVerification.vehicleInsuranceUrl) && (
                <div className="border-t-2 border-gray-200 pt-6 space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Driver Verification Documents
                  </h3>
                  {[
                    {
                      label: 'Driver License (Front)',
                      url: selectedVerification.driverLicenseFrontUrl,
                      name: selectedVerification.driverLicenseFrontName,
                    },
                    {
                      label: 'Driver License (Back)',
                      url: selectedVerification.driverLicenseBackUrl,
                      name: selectedVerification.driverLicenseBackName,
                    },
                    {
                      label: 'Vehicle Insurance Policy',
                      url: selectedVerification.vehicleInsuranceUrl,
                      name: selectedVerification.vehicleInsuranceName,
                    },
                  ]
                    .filter(doc => doc.url)
                    .map(doc => (
                      <div
                        key={doc.label}
                        className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200"
                      >
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <FileText size={24} className="text-gray-600" />
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                {doc.label}
                              </p>
                              <p className="text-sm font-medium text-gray-500">
                                File Name
                              </p>
                              <p className="text-lg text-gray-900">
                                {doc.name || 'Document'}
                              </p>
                            </div>
                          </div>
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-yellow-500 text-black px-4 py-2 rounded-lg hover:bg-yellow-600 font-semibold flex items-center space-x-2"
                            onClick={e => e.stopPropagation()}
                          >
                            <Eye size={18} />
                            <span>View Document</span>
                          </a>
                        </div>
                        <div className="mt-4">
                          {isImageUrl(doc.url) ? (
                            <img
                              src={doc.url}
                              alt={`${doc.label} preview`}
                              className="max-w-full h-auto rounded-lg border-2 border-gray-300"
                            />
                          ) : (
                            <div className="bg-white p-8 rounded-lg border-2 border-gray-300 text-center">
                              <FileText
                                size={48}
                                className="mx-auto text-gray-400 mb-4"
                              />
                              <p className="text-gray-600">
                                PDF or other document format. Click "View
                                Document" to open.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              )}

              {/* Action Buttons */}
              {selectedVerification.driverVerificationStatus === 'pending' && (
                <div className="border-t-2 border-gray-200 pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Actions
                  </h3>
                  <div className="space-y-4">
                    {/* Rejection Comment */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Rejection Comment (Required for rejection)
                      </label>
                      <textarea
                        value={rejectionComment}
                        onChange={e => setRejectionComment(e.target.value)}
                        rows={4}
                        className="w-full px-4 py-2 border-2 border-black rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
                        placeholder="Enter reason for rejection..."
                      />
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => handleApprove(selectedVerification)}
                        disabled={isProcessing}
                        className="flex-1 bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                      >
                        <CheckCircle size={20} />
                        <span>Approve</span>
                      </button>
                      <button
                        onClick={() => handleReject(selectedVerification)}
                        disabled={isProcessing || !rejectionComment.trim()}
                        className="flex-1 bg-red-600 text-white py-3 px-4 rounded-lg hover:bg-red-700 font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                      >
                        <XCircle size={20} />
                        <span>Reject</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverVerifications;
